import JSZip from 'jszip';

// Form file category for each supported extension, matching the Documents cards.
const CATEGORY_BY_EXTENSION = {
  pdf: 'pdfFiles',
  doc: 'wordFiles',
  docx: 'wordFiles',
  txt: 'textFiles',
  ppt: 'pptFiles',
  pptx: 'pptFiles',
  xls: 'excelFiles',
  xlsx: 'excelFiles',
  xlsm: 'excelFiles',
  csv: 'csvFiles'
};

// The presigned S3 URL is signed with the Content-Type the backend picks for the
// category (_get_content_type), so files taken out of a zip carry that same type.
const CONTENT_TYPE_BY_CATEGORY = {
  pdfFiles: 'application/pdf',
  wordFiles: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  pptFiles: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  excelFiles: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  csvFiles: 'text/csv',
  textFiles: 'text/plain'
};

const getExtension = (name) => {
  const lastDotIndex = name.lastIndexOf('.');
  return lastDotIndex > 0 ? name.substring(lastDotIndex + 1).toLowerCase() : '';
};

// macOS resource forks and hidden files (.DS_Store, ._name) that zips and folders carry along
const isHiddenPath = (path) =>
  path.split('/').some(part => part === '__MACOSX' || part.startsWith('.'));

const hashBuffer = async (buffer) => {
  if (!window.crypto?.subtle) return null;
  const digest = await window.crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('');
};

// Reads every entry of a dropped folder; readEntries returns them in batches.
const readAllEntries = (reader) => new Promise((resolve, reject) => {
  const entries = [];
  const readBatch = () => reader.readEntries(batch => {
    if (batch.length === 0) {
      resolve(entries);
    } else {
      entries.push(...batch);
      readBatch();
    }
  }, reject);
  readBatch();
});

const collectEntryFiles = async (entry) => {
  if (entry.isFile) {
    const file = await new Promise((resolve, reject) => entry.file(resolve, reject));
    return [{ file, path: entry.fullPath.replace(/^\//, '') }];
  }
  if (!entry.isDirectory) return [];
  const children = await readAllEntries(entry.createReader());
  const nested = await Promise.all(children.map(collectEntryFiles));
  return nested.flat();
};

// Files from a drag and drop, with every dropped folder walked to its files.
export const getDroppedFiles = async (dataTransfer) => {
  // Entries must be taken before the first await, or the browser empties the list.
  const entries = Array.from(dataTransfer.items || [])
    .map(item => item.webkitGetAsEntry && item.webkitGetAsEntry())
    .filter(Boolean);
  if (entries.length === 0) {
    return Array.from(dataTransfer.files || []).map(file => ({ file, path: file.name }));
  }
  const nested = await Promise.all(entries.map(collectEntryFiles));
  return nested.flat();
};

// Files picked from a zip or folder input.
export const getPickedFiles = (fileList) =>
  Array.from(fileList || []).map(file => ({ file, path: file.webkitRelativePath || file.name }));

// Sorts loose files, folder files and zips into the form's file categories.
// Zips are opened wherever they sit, at any depth: a queue rather than recursion
// keeps deep nesting off the call stack. Returns { documents, skipped }, where
// documents is { pdfFiles: [File], ... } and skipped is [{ path, reason }].
export const extractDocuments = async (items, onProgress) => {
  const documents = {};
  const skipped = [];
  const openedArchives = new Set();
  const queue = [...items];
  let found = 0;

  const addDocument = (blob, path, category) => {
    const name = path.split('/').pop();
    const file = new File([blob], name, { type: CONTENT_TYPE_BY_CATEGORY[category] });
    (documents[category] = documents[category] || []).push(file);
    found += 1;
    if (onProgress) onProgress(found);
  };

  while (queue.length > 0) {
    const { file, path } = queue.shift();
    if (isHiddenPath(path)) continue;

    const extension = getExtension(path.split('/').pop());

    if (extension !== 'zip') {
      const category = CATEGORY_BY_EXTENSION[extension];
      if (category) {
        addDocument(file, path, category);
      } else {
        skipped.push({ path, reason: 'Unsupported file type' });
      }
      continue;
    }

    try {
      const buffer = await file.arrayBuffer();
      // A zip that contains itself would otherwise never finish
      const archiveHash = await hashBuffer(buffer);
      if (archiveHash && openedArchives.has(archiveHash)) {
        skipped.push({ path, reason: 'Same zip already opened' });
        continue;
      }
      if (archiveHash) openedArchives.add(archiveHash);

      const zip = await JSZip.loadAsync(buffer);
      for (const entry of Object.values(zip.files)) {
        if (entry.dir) continue;
        const entryPath = `${path}/${entry.name}`;
        if (isHiddenPath(entry.name)) continue;

        const entryExtension = getExtension(entry.name.split('/').pop());
        const category = CATEGORY_BY_EXTENSION[entryExtension];
        if (entryExtension !== 'zip' && !category) {
          skipped.push({ path: entryPath, reason: 'Unsupported file type' });
          continue;
        }

        try {
          const blob = await entry.async('blob');
          if (category) {
            addDocument(blob, entryPath, category);
          } else {
            queue.push({ file: blob, path: entryPath });
          }
        } catch (entryError) {
          skipped.push({ path: entryPath, reason: 'Could not read file from zip' });
        }
      }
    } catch (archiveError) {
      skipped.push({ path, reason: 'Could not open zip (corrupt or password protected)' });
    }
  }

  return { documents, skipped };
};
